import { View } from 'react-native'
import { Redirect, Stack, usePathname } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { RetrySignOutErrorWall } from '../components/ui/RetrySignOutErrorWall'
import { ScreenLoadingFallback } from '../components/ui/ScreenLoadingFallback'
import { useAppSession } from '../contexts/AppSessionContext'
import { extractApiErrorMessage } from '../utils/parseApiError'

function isWithinPurchasePath(pathname: string): boolean {
  return pathname === '/purchase' || pathname.startsWith('/purchase/')
}

export function AccountNavigator() {
  const { t } = useTranslation(['common'])
  const pathname = usePathname()
  const { accountRoute, backendUserError, logout, refreshUserStatus } =
    useAppSession()

  if (accountRoute === 'loading') {
    return <ScreenLoadingFallback />
  }

  if (accountRoute === 'onboarding') {
    return <Redirect href="/onboarding" />
  }

  if (accountRoute === 'create-user') {
    return <Redirect href="/create-user" />
  }

  if (accountRoute === 'purchase' && !isWithinPurchasePath(pathname)) {
    return <Redirect href="/purchase" />
  }

  const errorMessage =
    extractApiErrorMessage(backendUserError) ?? t('common:error')

  if (accountRoute === 'error') {
    return (
      <RetrySignOutErrorWall
        message={errorMessage}
        onRetry={refreshUserStatus}
        onSignOut={logout}
      />
    )
  }

  const allowsMainContent = accountRoute === 'main'
  const allowsPaywall = accountRoute === 'main' || accountRoute === 'purchase'
  const allowsPostPaymentOnboarding = accountRoute === 'post-payment-onboarding'
  const allowsVoiceAssessment = allowsMainContent || allowsPostPaymentOnboarding

  return (
    <View
      className="flex-1"
      testID={allowsMainContent ? 'e2e-authenticated-root' : undefined}
    >
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Protected guard={allowsPostPaymentOnboarding}>
          <Stack.Screen name="post-payment-onboarding" />
        </Stack.Protected>
        <Stack.Protected guard={allowsMainContent}>
          <Stack.Screen name="(tabs)" />
          <Stack.Screen name="voice-assessment-history" />
          <Stack.Screen name="report-issue" />
          <Stack.Screen name="preview/[id]" />
          <Stack.Screen name="session/[id]" />
          <Stack.Screen name="session/finish" />
        </Stack.Protected>
        <Stack.Protected guard={allowsVoiceAssessment}>
          <Stack.Screen name="voice-assessment/index" />
          <Stack.Screen name="voice-assessment/[sessionId]" />
          <Stack.Screen name="voice-assessment-finish" />
        </Stack.Protected>
        <Stack.Protected guard={allowsPaywall}>
          <Stack.Screen name="(paywall)/purchase" />
        </Stack.Protected>
      </Stack>
    </View>
  )
}
